import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useInjection } from 'inversify-react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { EyeCheckboxButton, PenCheckboxButton } from '@/components/boxes/CheckboxButton';
import { TYPES } from '@/engine/ioc';
import { TYPES as IndicationTypes } from '@/engine/app/modules/indications/iocTypes.ts';
import { useTheme } from '@mui/material/styles';
import {
  StyledTableCell,
  UpdatableUserConfirmationRequiredIcon,
  UpdatableResetIconButton
} from '@/components/boxes/BoxTools.tsx';
import { useHighlight } from '@/components/boxes/hooks/useHighlight.ts';
import { useSelect } from '@/components/boxes/hooks/useSelect.ts';
import { getConditionalStyles } from '@/components/boxes/styles.ts';
import { IconButton, Tooltip } from '@mui/material';
import { observer } from 'mobx-react';
import { EditableCellTextField } from '@/components/common/EditableCellTextField.tsx';
import { ButtonsContainer, Container } from '@/components/boxes/box-containers/boxContainers.ts';
import { useDisposableController } from '@/components/boxes/hooks/useDisposableController.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { IndicationBoxBasicBoxProps, MeasurementProps } from '@/components/boxes/measurementBoxes/types.ts';
import type { IMeasurementViewModel } from '@/engine/app/interfaces.ts';
import type { MeasurementBoxesController } from '@/components/boxes/measurementBoxes/MeasurementBoxesController/MeasurementBoxes.controller.ts';
import { type BoxesIndicationStore } from '@/components/boxes/BoxesIndicationStore.ts';
import { type IReactionDisposer, reaction } from 'mobx';
import type { VariableDistancesBoxController } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/BoxController/VariableDistancesBox.controller.ts';
import { UpdatableValue } from '@/components/boxes/common/UpdatableValue.tsx';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import { EyeCheckboxRender } from '@/components/common/expandableTable/EyeCheckboxRender.tsx';
import { PenCheckboxRender } from '@/components/common/expandableTable/PenCheckboxRender';

type DistanceMeasurementBoxProps = IndicationBoxBasicBoxProps<MeasurementProps>;

export const VariableDistanceMeasurementBox: React.FC<DistanceMeasurementBoxProps> = observer(
  ({ measurements, group }) => {
    const theme = useTheme();

    if (!group) throw new Error('Group is must for Variable distance measurement box');

    const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
    const boxesIndicationStore = useInjection<BoxesIndicationStore>(IndicationTypes.BoxesIndicationStore);

    const vdBoxController = useDisposableController(
      useInjection<VariableDistancesBoxController>(TYPES.VariableDistancesBoxController),
      group
    );

    const viewsService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);

    const measurementVMs: Array<IMeasurementViewModel> = viewsService.currentViewOrThrow.getMeasurements();

    const { isHighlighted } = useHighlight(measurementVMs);
    const { isSelected } = useSelect(measurementVMs);

    const handleAddClick = useCallback((): void => {
      boxesIndicationStore.setCurrentIndication(true);

      if (vdBoxController.isIndicating) return;

      vdBoxController.setIndicationState(!vdBoxController.isIndicating);
    }, [vdBoxController, boxesIndicationStore]);

    useEffect(() => {
      const disposer: IReactionDisposer = reaction(
        () => boxesIndicationStore.isOn,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
        async (): Promise<void> => {
          if (vdBoxController.isIndicating) return vdBoxController.cancelIndicating();
        }
      );

      return (): void => disposer();
    }, [boxesIndicationStore.isOn, vdBoxController]);

    const allDescriptors = useMemo(() => measurements.map((m) => m.descriptor), [measurements]);

    return (
      <Container>
        <ButtonsContainer>
          <Tooltip title="Indicate new distance" placement="top">
            <span>
              <IconButton
                data-testid="indicateNewDistance"
                disabled={!vdBoxController.isIndicating && measurements.length > 4}
                aria-label="indicate"
                color={vdBoxController.isIndicating ? 'primary' : 'secondary'}
                onClick={handleAddClick}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Toggle edit mode" placement="top">
            <span>
              <PenCheckboxRender
                onChange={(checked: boolean): void =>
                  mainController.updateAllViewItemsBeingEdited(allDescriptors, checked)
                }
                isChecked={mainController.checkGroupViewItemsBeingEdited(allDescriptors)}
                isEmptyBox={measurements.length === 0}
              />
            </span>
          </Tooltip>
          <Tooltip title="Toggle visibility" placement="top">
            <span>
              <EyeCheckboxRender
                onChange={(isVisible: boolean): void => {
                  mainController.updateAllViewItemsVisibility(allDescriptors, isVisible);
                }}
                isChecked={mainController.checkGroupViewItemsVisibility(allDescriptors)}
                isEmptyBox={measurements.length === 0}
              />
            </span>
          </Tooltip>
        </ButtonsContainer>
        <TableContainer component={Paper}>
          <Table aria-label="papillary muscles box" size="small">
            <TableBody>
              {measurements.map((measurement: MeasurementProps, index) => {
                const isItemSelected = isSelected(measurement.descriptor);
                const isItemHighlighted = isHighlighted(measurement.descriptor);

                return (
                  <TableRow
                    aria-label={measurement.label}
                    key={measurement.descriptor}
                    data-testid={measurement.descriptor + 'Distance'}
                    sx={{
                      ...{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover button.locate-button': {
                          opacity: 1
                        },
                        '& button.locate-button': { opacity: 0.1 }
                      },
                      ...getConditionalStyles(
                        isItemHighlighted,
                        theme,
                        { paddingLeft: 0.3, paddingTop: 0, paddingBottom: 0 },
                        {
                          paddingRight: 0.5,
                          paddingTop: 0,
                          paddingBottom: 0
                        }
                      )
                    }}
                    onMouseEnter={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, true)}
                    onMouseLeave={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, false)}
                    selected={isItemSelected}
                    onClick={(): void => {
                      mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
                    }}
                  >
                    <TableCell component="th" scope="row" key={'label' + index}>
                      {measurement.imported ? (
                        <span>{measurement.label}</span>
                      ) : (
                        <EditableCellTextField
                          value={measurement.label}
                          onBlur={(e): void => {
                            vdBoxController.setLabel(measurement.descriptor, e.currentTarget.value);
                          }}
                        />
                      )}
                    </TableCell>
                    <StyledTableCell className="limited-width light-fill" align="center">
                      {measurement.imported ? (
                        <UpdatableResetIconButton descriptor={measurement.descriptor} controller={mainController} />
                      ) : (
                        <UpdatableUserConfirmationRequiredIcon
                          descriptor={measurement.descriptor}
                          controller={mainController}
                        />
                      )}
                    </StyledTableCell>
                    <TableCell
                      size="small"
                      align="right"
                      key={'value' + index}
                      sx={{ '&': { paddingRight: 0, width: '4rem' } }}
                    >
                      <UpdatableValue
                        descriptor={measurement.descriptor}
                        constructor={DT.DistanceMeasurement}
                        valueKey="distance"
                        isUndefinedOk={true}
                      />{' '}
                      mm
                    </TableCell>
                    <StyledTableCell
                      align="right"
                      key={'delete' + index}
                      className="limited-width"
                      onClick={(event): void => event.stopPropagation()}
                    >
                      <Tooltip title="Delete" placement="left" hidden={measurement.imported}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
                            onClick={async (): Promise<void> => {
                              await vdBoxController.deleteVariableDistanceByDescriptor(measurement.descriptor);
                            }}
                          >
                            <DeleteOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell align="right" key={'beingEdited' + index} className="limited-width">
                      {measurement.beingEdited !== undefined ? (
                        <PenCheckboxButton
                          onChange={(checked): void => {
                            mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                          }}
                          checked={measurement.beingEdited}
                          key={'beingEditedBtn' + index}
                        />
                      ) : (
                        <div />
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      key={'visibility' + index}
                      className="limited-width"
                      onClick={(event): void => event.stopPropagation()}
                    >
                      <EyeCheckboxButton
                        onChange={(checked): void =>
                          mainController.setMeasurementVisibility(measurement.descriptor, checked)
                        }
                        checked={measurement.visible}
                      />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    );
  }
);
